import { useEffect, useState } from 'react';
import { supabase } from '@/lib/supabase';

export interface TipsTopic {
  id: string;
  name: string;
  slug: string;
  sort_order: number;
  created_at: string;
}

export function useTipsTopics() {
  const [topics, setTopics] = useState<TipsTopic[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchTopics();

    const channel = supabase
      .channel('tips-topics-changes')
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'tips_topics' },
        fetchTopics,
      )
      .subscribe();

    return () => {
      channel.unsubscribe();
    };
  }, []);

  async function fetchTopics() {
    const { data } = await supabase
      .from('tips_topics')
      .select('*')
      .order('sort_order');
    setTopics(data ?? []);
    setLoading(false);
  }

  async function createTopic(name: string, slug: string) {
    const existing = topics.find((t) => t.slug === slug);
    if (existing) throw new Error(`Er bestaat al een topic met de naam '${existing.name}'.`);
    const maxOrder = topics.reduce((max, t) => Math.max(max, t.sort_order), -1);
    const { error } = await supabase
      .from('tips_topics')
      .insert({ name, slug, sort_order: maxOrder + 1 });
    if (error) {
      if (error.message?.includes('duplicate key')) {
        throw new Error(`Er bestaat al een topic met deze slug '${slug}'.`);
      }
      throw error;
    }
  }

  async function updateTopic(id: string, name: string, slug: string) {
    const { error } = await supabase
      .from('tips_topics')
      .update({ name, slug })
      .eq('id', id);
    if (error) throw error;
  }

  async function deleteTopic(id: string) {
    const { error } = await supabase
      .from('tips_topics')
      .delete()
      .eq('id', id);
    if (error) throw error;
  }

  async function getTopicsForContent(contentId: string): Promise<string[]> {
    const { data } = await supabase
      .from('tips_content_topics')
      .select('keyword_id')
      .eq('content_id', contentId);
    return (data ?? []).map((r) => r.keyword_id);
  }

  async function setTopicsForContent(contentId: string, topicIds: string[]) {
    await supabase
      .from('tips_content_topics')
      .delete()
      .eq('content_id', contentId);
    if (topicIds.length > 0) {
      const { error } = await supabase
        .from('tips_content_topics')
        .insert(topicIds.map((tid) => ({ content_id: contentId, keyword_id: tid })));
      if (error) throw error;
    }
  }

  return {
    topics,
    loading,
    createTopic,
    updateTopic,
    deleteTopic,
    getTopicsForContent,
    setTopicsForContent,
  };
}
